import { formatCell, recordCells, type CellView } from './columns';

import type { BulkModule, ColumnDef, RecordRow, RunView } from '../schemas/bulk-migration';

/**
 * Which modules the picker offers, and which can be launched (INI-0035 D3).
 *
 * The runner may list an entry that runs other modules in order — "Contacts and
 * households" runs Households, then Contacts, per client — next to the modules
 * it runs, which it keeps listing so an older screen still works. This screen
 * offers the entry and hides the modules it runs: an operator launches the one
 * action, and the runner chains the rest. A member's own page is history and
 * records only: no Analyze, no Migrate. Relaunching the composite is how a step
 * is redone (a changed-only Households pass writes nothing when nothing changed).
 *
 * The rule is generic, never a list of ids: a module is hidden when a
 * COMPOSITE entry (`kind: composite`, with members) names it. `members` on any
 * other entry is ignored, an entry is never hidden because it names itself, and
 * an entry that is itself a composite is never hidden (so a cycle hides
 * nothing). A runner from before composites sends no `members`, so nothing is
 * hidden and the picker is the one it always was. A hidden module is still in
 * the list the page resolves `?module=` against, so a link to it (its own
 * history and reports) still opens.
 */

/** Whether the entry runs other modules (its `kind`, with at least one member). */
export function isComposite(module: Pick<BulkModule, 'kind' | 'members'>): boolean {
    return module.kind === 'composite' && (module.members?.length ?? 0) > 0;
}

/** The modules some composite runs, by id: never a composite, never the composite itself. */
export function memberIds(modules: readonly BulkModule[]): ReadonlySet<string> {
    const composites = new Set(modules.filter(isComposite).map((module) => module.moduleId));
    const ids = new Set<string>();
    for (const module of modules) {
        if (!isComposite(module)) continue;
        for (const member of module.members ?? []) {
            if (!composites.has(member)) ids.add(member);
        }
    }
    return ids;
}

/** The picker's modules: every entry except the ones a composite runs, in the runner's order. */
export function pickableModules(modules: readonly BulkModule[]): BulkModule[] {
    const hidden = memberIds(modules);
    return modules.filter((module) => !hidden.has(module.moduleId));
}

/**
 * The composite that runs this module, or `undefined` when none does. A module
 * with one is history and records only on its own page.
 */
export function composerOf(
    moduleId: string,
    modules: readonly BulkModule[],
): BulkModule | undefined {
    if (!memberIds(modules).has(moduleId)) return undefined;
    return modules.find(
        (module) => isComposite(module) && (module.members ?? []).includes(moduleId),
    );
}

/** Whether the module's own page may Analyze and Migrate (no composite runs it). */
export function isLaunchable(moduleId: string, modules: readonly BulkModule[]): boolean {
    return composerOf(moduleId, modules) === undefined;
}

export interface CompositeStep {
    readonly moduleId: string;
    readonly title: string;
}

/**
 * What a composite runs, in order, with each member's title; `[]` for a plain
 * module. A member the list does not hold is left out: there is no page to
 * link it to.
 */
export function compositeSteps(
    module: Pick<BulkModule, 'kind' | 'members'>,
    modules: readonly BulkModule[],
): CompositeStep[] {
    if (!isComposite(module)) return [];
    return (module.members ?? []).flatMap((moduleId) => {
        const member = modules.find((candidate) => candidate.moduleId === moduleId);
        return member === undefined ? [] : [{ moduleId, title: member.title }];
    });
}

/** "Runs Households, then Contacts", or `null` when there is nothing to name. */
export function compositeSummary(steps: readonly CompositeStep[]): string | null {
    if (steps.length === 0) return null;
    return `Runs ${steps.map((step) => step.title).join(', then ')}`;
}

/** Member module id → title, for the tag on a composite's records. */
export function memberTitles(steps: readonly CompositeStep[]): ReadonlyMap<string, string> {
    return new Map(steps.map((step) => [step.moduleId, step.title]));
}

/**
 * The tag a record of a composite row carries: the member it belongs to, by
 * title when known, else its id. `null` on a plain module's record (no `moduleId`).
 */
export function recordModuleTag(
    record: Pick<RecordRow, 'moduleId'>,
    titles: ReadonlyMap<string, string>,
): string | null {
    if (record.moduleId === undefined) return null;
    return titles.get(record.moduleId) ?? record.moduleId;
}

/**
 * The member a composite row's run belongs to, when the runner names one: a launch
 * that found a member already running follows THAT run (`run.moduleId`). By title
 * when known, else its id; `null` when the run is the job's own module's.
 */
export function runModuleTag(
    run: Pick<RunView, 'moduleId'> | undefined,
    titles: ReadonlyMap<string, string>,
): string | null {
    if (run?.moduleId === undefined) return null;
    return titles.get(run.moduleId) ?? run.moduleId;
}

/**
 * A record's cells in the clients table. A composite's columns are its members'
 * merged by key, and each of its records (tagged with `moduleId`) carries only
 * its own member's keys: a key such a record does not carry reads blank (`''`),
 * not as an empty value (`—`). A plain module's record reads as it always did.
 */
export function rowRecordCells(
    columns: readonly ColumnDef[],
    record: Pick<RecordRow, 'columns' | 'moduleId'>,
): CellView[] {
    if (record.moduleId === undefined) return recordCells(columns, record);
    return columns.map((column) => ({
        key: column.key,
        label: column.label,
        align: column.align ?? 'left',
        text: Object.hasOwn(record.columns, column.key)
            ? formatCell(record.columns[column.key])
            : '',
    }));
}
